/**
 * Accommodation List PDF Layout
 *
 * Creates a simple table layout for accommodation bookings per specification.
 * Shows hotel/lodging details with dates, addresses and contact information.
 * Basic accommodation information as displayed - NO statistics or complex sections.
 */

export class AccommodationListPDFLayout {
  constructor() {
    this.layoutType = 'table';
    this.name = 'Accommodation List Layout';
  }

  /**
   * Generate PDF content structure for accommodation list
   * @param {Object} pageContext - Page context with accommodation data
   * @returns {Object} PDF content structure
   */
  async generate(pageContext) {
    const accommodationData = Array.isArray(pageContext.data) ? pageContext.data : [];

    if (accommodationData.length === 0) {
      return this.generateEmptyContent(pageContext);
    }

    // Sort accommodations by check-in date
    const sortedAccommodations = this.sortAccommodationsByDate(accommodationData);

    return {
      sections: [
        this.generateAccommodationListSection(sortedAccommodations, pageContext)
      ].filter(section => section !== null)
    };
  }


  /**
   * Generate main accommodation list section - SPEC COMPLIANT SIMPLE TABLE
   * Per specification: "Hotel/lodging details with dates, addresses and contact information"
   * Basic accommodation information as displayed - ONE simple table only
   * @param {Array} accommodationData - Sorted accommodation data
   * @param {Object} pageContext - Page context
   * @returns {Object} Accommodation list section
   */
  generateAccommodationListSection(accommodationData, pageContext) {
    return {
      type: 'table',
      title: null, // No title per spec - clean simple table
      columns: ['Name', 'Address', 'Check-in/Check-out Dates', 'Contact Info'],
      rows: accommodationData.map(accommodation => [
        accommodation.details?.name || accommodation.summary || 'Unnamed Accommodation',
        this.formatLocation(accommodation),
        this.formatStayDates(accommodation),
        this.formatContactInfo(accommodation)
      ])
    };
  }



  /**
   * Generate content for empty accommodation list
   * @param {Object} pageContext - Page context
   * @returns {Object} Empty content structure
   */
  generateEmptyContent(pageContext) {
    return {
      sections: [{
        type: 'text',
        title: 'No Accommodation Booked',
        content: 'This trip does not have any accommodation bookings yet. Add some hotel or lodging reservations to generate a more detailed PDF export.'
      }]
    };
  }

  /**
   * Sort accommodations by check-in date
   * @param {Array} accommodationData - Accommodation data
   * @returns {Array} Sorted accommodations
   */
  sortAccommodationsByDate(accommodationData) {
    return accommodationData
      .filter(acc => acc && acc.details?.checkInDate)
      .sort((a, b) => new Date(a.details.checkInDate) - new Date(b.details.checkInDate));
  }


  /**
   * Format contact information - phone and website only
   * @param {Object} accommodation - Accommodation object
   * @returns {string} Contact info string
   */
  formatContactInfo(accommodation) {
    const info = [];

    // Add phone number if available
    if (accommodation.details?.phone) {
      info.push(`Tel: ${accommodation.details.phone}`);
    }

    // Add website URL if available
    if (accommodation.details?.website) {
      info.push(`Web: ${this.formatWebsite(accommodation.details.website)}`);
    }

    return info.length > 0 ? info.join(' | ') : '-';
  }

  /**
   * Format website URL for display
   * @param {string} website - Website URL
   * @returns {string} Formatted website
   */
  formatWebsite(website) {
    if (!website) return '';

    // Remove protocol for cleaner display
    return website.replace(/^https?:\/\//, '').replace(/^www\./, '');
  }

  /**
   * Format accommodation location
   * @param {Object} accommodation - Accommodation object
   * @returns {string} Formatted location
   */
  formatLocation(accommodation) {
    if (accommodation.details?.address) {
      return accommodation.details.address;
    }

    const parts = [];
    if (accommodation.details?.city) parts.push(accommodation.details.city);
    if (accommodation.details?.country) parts.push(accommodation.details.country);

    return parts.length > 0 ? parts.join(', ') : 'Location not specified';
  }

  /**
   * Format stay dates - simple format per spec
   * @param {Object} accommodation - Accommodation object
   * @returns {string} Formatted dates
   */
  formatStayDates(accommodation) {
    if (!accommodation.details?.checkInDate) {
      return 'Dates not specified';
    }

    const checkIn = this.formatDate(accommodation.details.checkInDate);

    if (!accommodation.details?.checkOutDate) {
      return `Check-in: ${checkIn}`;
    }

    const checkOut = this.formatDate(accommodation.details.checkOutDate);

    return `${checkIn} to ${checkOut}`;
  }





  /**
   * Format date for display
   * @param {string} dateString - Date string
   * @returns {string} Formatted date
   */
  formatDate(dateString) {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Calculate number of nights between dates
   * @param {Object} accommodation - Accommodation object with details
   * @returns {number} Number of nights
   */
  calculateNights(accommodation) {
    try {
      if (!accommodation.details?.checkInDate || !accommodation.details?.checkOutDate) {
        return 0;
      }

      const start = new Date(accommodation.details.checkInDate);
      const end = new Date(accommodation.details.checkOutDate);
      const diffTime = end - start;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch (error) {
      return 0;
    }
  }

  /**
   * Get preview information for this layout - simplified per spec
   * @param {*} data - Data to analyze
   * @returns {Object} Preview information
   */
  getPreview(data) {
    const accommodationCount = Array.isArray(data) ? data.length : 0;

    return {
      estimatedPages: Math.max(1, Math.ceil(accommodationCount / 8)), // Simple table estimate
      sections: ['Accommodation List'], // Only one simple table
      features: [
        'Hotel/lodging details with dates',
        'Addresses and contact information',
        'Basic accommodation information as displayed'
      ],
      dataCount: accommodationCount
    };
  }
}